import { Injectable } from '@angular/core';
import { AngularFireDatabase } from 'angularfire2/database';
import { AngularFirestore } from 'angularfire2/firestore';
import { map, switchMap } from 'rxjs/operators';
import { isEmpty, has } from 'lodash';
import { Observable } from 'rxjs/Observable';
import { ENV } from '@app/env';
import 'rxjs/add/observable/fromPromise';
import * as firebase from 'firebase';
import { IVoting } from '../models/voting.model';
import { UtilitiesProvider } from './utilities';

@Injectable()
export class VotingData {

  constructor(
    private afdb: AngularFireDatabase,
    private afStore: AngularFirestore,
    private utilities: UtilitiesProvider

  ) { }

  public getEventActiveVoting(clientId: string, eventId: string) {
    return this.afdb.object(`/Clients/${clientId}/EventConfig/${eventId}/interactivity/Voting`).valueChanges()
  }

  public getVotingByClientId(clientId: string, votingId: string): Observable<IVoting> {
    return this.afdb.object<IVoting>(`/Clients/${clientId}/Voting/${votingId}`).valueChanges()
  }

  public getVotingPonderationByUserId(clientId: string, votingId: string, ponderationType: 'email' | 'code', value: any): Observable<any> {
    return this.afdb.list(`/Clients/${clientId}/Voting/${votingId}/ponderation/records`, ref => {
      return ref.orderByChild(`${ponderationType}`).equalTo(value);
    }).valueChanges().pipe(map((data) => {
      this.utilities.consoleDecorator('ponderationType', ponderationType);
      switch (ponderationType) {
        case 'email':
          if (!isEmpty(data) && data[0]['state'] === true) {
            return data[0];
          } else {
            return null;
          }
        default:
          return !isEmpty(data) ? data[0] : null;
      }
    }))
  }

  public getRecordPonderationById(clientId: string, eventId: string, votingId: string, ponderationId: string) {
    return this.afdb.object(`/Clients/${clientId}/Voting/${votingId}/ponderation/records/${ponderationId}`).valueChanges()
  }

  public hasPonderationRegisterByUserId(clientId: string, eventId: string, votingId: string, userId: string) {
    return this.afStore.collection(ENV.client).doc('voting-event-registers').collection('event').doc(eventId).collection('voting').doc(votingId)
      .collection('codes', ref => {
        return ref.where('userId', '==', userId).where('votingId', '==', votingId)
      }).valueChanges()
      .pipe(switchMap((data: any) => {
        this.utilities.consoleDecorator('hasPonderationRegisterByUserId', data);
        if (!isEmpty(data)) {
          return this.getRecordPonderationById(clientId, eventId, votingId, data[0].ponderationId);
        } else {
          return Observable.of({})
        }
      }))
  }

  public canShowActiveEventVotingToUser(clientId: string, eventId: string, votingId: string, userId: string) {
    return this.afStore.collection(clientId).doc('voting-event-views').collection('event').doc(eventId).collection('voting').doc(votingId).collection('views', ref => {
      return ref.where('userId', '==', userId).where('votingId', '==', votingId)
    })
      .valueChanges()
      .pipe(
        map(userhasRecord => {
          this.utilities.consoleDecorator('userhasRecord', userhasRecord);
          return (!isEmpty(userhasRecord)) ? { show: false } : { show: true, votingId: votingId };
        }));
  }

  public getUserVotingEventAnswer(clientId: string, eventId: string, votingId: string, userId: string) {
    return this.afStore.collection(clientId).doc('voting-events')
      .collection(`${votingId}`).doc('event')
      .collection(`${eventId}`, ref => {
        return ref.where('userId', '==', userId).where('votingId', '==', votingId)
      })
      .valueChanges()
      .map((data) => {
        return (isEmpty(data)) ? data : data[0];
      });
  }

  public saveEventVotingView(eventId: string, votingId: string,
    userId: string): Observable<any> {
    let id = this.afdb.createPushId();
    return Observable.fromPromise(this.afStore.collection(ENV.client).doc('voting-event-views').collection('event').doc(eventId).collection('voting').doc(votingId).collection('views')
      .doc(<string>id).set({
        id: id,
        create_at: firebase.firestore.FieldValue.serverTimestamp(),
        votingId: votingId,
        userId: userId,
        eventId: eventId
      }))
  };

  public saveEventVotingStatistic(eventId: string, votingId: string) {
    return Observable.fromPromise(this.afdb.object(`/Clients/${ENV.client}/Events/${eventId}/stats/voting/${votingId}`)
      .query.ref.transaction((votingStats) => {
        if (has(votingStats, 'views')) {
          return { ...votingStats, views: votingStats.views + 1 };
        } else {
          return {
            views: 1
          }
        }
      }));
  }

  public saveEventVotingStatisticAndView(eventId: string, votingId: string, userId: string) {
    return this.saveEventVotingStatistic(eventId, votingId)
      .pipe(switchMap(() => {
        return this.saveEventVotingView(eventId, votingId, userId)
      }))
  }

  public saveEventVotingAnswer(clientId: string, eventId: string, votingId: string, userId: string, userPonderation: number, answers: any) {
    return this.afStore.collection(clientId).doc('voting-events').collection(`${votingId}`).doc('event')
      .collection(`${eventId}`, ref => {
        return ref.where('votingId', '==', votingId).where('userId', '==', userId)
      }).valueChanges().pipe(
        (map((userAnswer: any) => {
          if (!isEmpty(userAnswer)) {
            return Observable.fromPromise(this.afStore.collection(clientId).doc('voting-events').collection(`${votingId}`).doc('event')
              .collection(`${eventId}`).doc(<string>userAnswer[0].id).set({
                "id": userAnswer[0].id,
                "votingId": votingId,
                "userId": userId,
                "answers": answers,
                "ponderation": userPonderation
              }));
          } else {
            let id = this.afdb.createPushId();
            return Observable.fromPromise(this.afStore.collection(clientId).doc('voting-events').collection(`${votingId}`).doc('event')
              .collection(`${eventId}`).doc(<string>id).set({
                "id": id,
                "votingId": votingId,
                "userId": userId,
                "answers": answers,
                "ponderation": userPonderation
              }));
          }

        }))
      )
  }

  public validateVotingCode(eventId: string, votingId: string, userId: string, codeValue: string): Observable<any> {
    return this.getVotingPonderationByUserId(ENV.client, votingId, 'code', codeValue)
      .pipe(switchMap((data) => {
        if (data) {
          if (data.uses < data.quota) {
            return this.saveEventVotingRegister(eventId, votingId, data.id)
              .switchMap(() => {
                return this.saveEventVotingCodeRegister(eventId, votingId, userId, data.id)
              }).switchMap(() => {
                return Observable.of({ status: 'valid', message: 'Registration was successfull' })
              })
          } else {
            return Observable.of({ status: 'invalid', message: 'Maximum capacity of registration by this code' })
          }
        } else {
          this.utilities.consoleError('Invalid Code', data);
          return Observable.of({ status: 'undefined', message: 'The code is not valid' })
        }
      }))
  }



  public saveEventVotingRegister(eventId: string, votingId: string, ponderationId: string) {
    let obs = Observable.fromPromise(firebase.database().ref(`/Clients/${ENV.client}/Voting/${votingId}/ponderation/records/${ponderationId}/uses`)
      .transaction((ponderationInfo) => {
        return ponderationInfo + 1;
      })).map((data) => {
        return data;
      });
    return obs;
  }

  public saveEventVotingEmailRegister(eventId: string, votingId: string,
    userId: string, userEmail: string): Observable<any> {
    let id = this.afdb.createPushId();
    return this.getVotingPonderationByUserId(ENV.client, votingId, 'email', userEmail)
      .pipe((switchMap((ponderationInfo) => {
        this.utilities.consoleDecorator('ponderationInfo', ponderationInfo);
        return Observable.fromPromise(this.afStore.collection(ENV.client).doc('voting-event-registers').collection('event').doc(eventId).collection('voting').doc(votingId)
          .collection('emails')
          .doc(<string>id).set({
            id: id,
            create_at: firebase.firestore.FieldValue.serverTimestamp(),
            votingId: votingId,
            ponderationId: ponderationInfo.id,
            userId: userId,
            eventId: eventId
          }))
      })))


  };

  public saveEventVotingCodeRegister(eventId: string, votingId: string,
    userId: string, ponderationId: string): Observable<any> {
    let id = this.afdb.createPushId();
    return Observable.fromPromise(this.afStore.collection(ENV.client).doc('voting-event-registers').collection('event').doc(eventId).collection('voting').doc(votingId)
      .collection('codes')
      .doc(<string>id).set({
        id: id,
        create_at: firebase.firestore.FieldValue.serverTimestamp(),
        votingId: votingId,
        ponderationId: ponderationId,
        userId: userId,
        eventId: eventId
      }))
  };

}



// WEBPACK FOOTER //
// ./src/providers/voting.ts